import React, { useState } from 'react';
import { Tag, Tooltip, Avatar, Progress } from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    MessageOutlined,
    PaperClipOutlined,
    UnorderedListOutlined,
    UserOutlined,
    SearchOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import WorkItemContextMenu from './modals/WorkItemContextMenu';
import { normalizeStatus } from '../../utils/normalizeStatus';

export default function MilestoneCardView({
    milestones = [],
    tasks = [],
    loading = false,
    onMilestoneClick,
    onStatusChange,
    onEdit,
    onDelete,
    onConvertType,
    onCreateMilestone,
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStatusFilter, setSelectedStatusFilter] = useState('All');

    // Filter milestones by search query & status filter
    const filteredMilestones = milestones.filter((ms) => {
        const titleMatch = (ms.title || ms.name || '').toLowerCase().includes(searchQuery.toLowerCase());
        const projectMatch = (ms.project?.name || '').toLowerCase().includes(searchQuery.toLowerCase());
        const matchesSearch = titleMatch || projectMatch;

        if (selectedStatusFilter === 'All') return matchesSearch;
        // Normalise both sides so legacy stored values match the canonical pill
        return matchesSearch && normalizeStatus(ms.status) === selectedStatusFilter;
    });

    const statusOptions = ['All', 'Backlog', 'In Progress', 'Under Review', 'Completed', 'On Hold', 'Cancelled'];

    const getStatusTag = (status) => {
        // Normalise before rendering so legacy values (Closed, Active, etc.) display correctly
        const s = normalizeStatus(status, 'Backlog');
        switch (s) {
            case 'Backlog':
                return <Tag color="default" className="rounded-lg px-2.5 py-0.5 font-bold text-xs" style={{ color: '#64748b', borderColor: '#cbd5e1', background: '#f1f5f9' }}>Backlog</Tag>;
            case 'In Progress':
                return <Tag color="processing" className="rounded-lg px-2.5 py-0.5 font-bold text-xs">In Progress</Tag>;
            case 'Under Review':
                return <Tag color="warning" className="rounded-lg px-2.5 py-0.5 font-bold text-xs">Under Review</Tag>;
            case 'Completed':
                return <Tag color="success" className="rounded-lg px-2.5 py-0.5 font-bold text-xs">Completed</Tag>;
            case 'On Hold':
                return <Tag color="orange" className="rounded-lg px-2.5 py-0.5 font-bold text-xs">On Hold</Tag>;
            case 'Cancelled':
                return <Tag color="error" className="rounded-lg px-2.5 py-0.5 font-bold text-xs">Cancelled</Tag>;
            default:
                return <Tag color="blue" className="rounded-lg px-2.5 py-0.5 font-bold text-xs">{s}</Tag>;
        }
    };

    return (
        <div className="space-y-6">
            {/* Top Toolbar: Search & Status Filter */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 bg-white dark:bg-[#1e293b] p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
                {/* Status Pills */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
                    {statusOptions.map((st) => {
                        const active = selectedStatusFilter === st;
                        return (
                            <button
                                key={st}
                                onClick={() => setSelectedStatusFilter(st)}
                                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs transition-all cursor-pointer whitespace-nowrap ${active
                                    ? 'bg-[#03488a] text-white shadow-xs'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                    }`}
                            >
                                {st}
                            </button>
                        );
                    })}
                </div>

                {/* Search Bar */}
                <div className="flex items-center gap-3 shrink-0">
                    <div className="relative flex-1 sm:w-64">
                        <SearchOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                        <input
                            type="text"
                            placeholder="Search milestones..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl pl-9 pr-4 py-2 text-xs text-slate-800 dark:text-slate-100 outline-none focus:border-[#03488a]"
                        />
                    </div>
                </div>
            </div>

            {/* Empty State */}
            {!loading && filteredMilestones.length === 0 && (
                <div className="bg-white dark:bg-[#1e293b] rounded-2xl p-12 text-center border border-slate-200 dark:border-slate-800 space-y-3">
                    <span className="material-symbols-outlined text-4xl text-amber-500">verified</span>
                    <h3 className="text-lg font-extrabold text-slate-800 dark:text-white">No Milestones Found</h3>
                    <p className="text-xs text-slate-500 max-w-md mx-auto">
                        {searchQuery || selectedStatusFilter !== 'All'
                            ? 'No milestones match your search filters.'
                            : 'There are no project milestones created yet.'}
                    </p>
                </div>
            )}

            {/* Milestones Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredMilestones.map((ms) => {
                    const msId = ms._id;
                    const title = ms.title || ms.name || 'Untitled Milestone';
                    const projectName = ms.project?.name || 'Project';

                    // Compute child tasks linked to this milestone
                    const childTasks = tasks.filter(
                        (t) => String(t.milestoneId || t.parentTask || '') === String(msId)
                    );
                    const completedChildTasks = childTasks.filter((t) => t.status === 'Completed').length;
                    const totalChildTasks = childTasks.length || ms.totalChildTasks || 0;

                    const progress = ms.progress ?? (totalChildTasks > 0 ? Math.round((completedChildTasks / totalChildTasks) * 100) : 0);

                    // Deliverables metrics
                    const deliverables = Array.isArray(ms.deliverables) ? ms.deliverables : [];
                    const completedDeliverables = deliverables.filter((d) => d.done).length;
                    const totalDeliverables = deliverables.length;

                    // Comments & Files counts
                    const commentsCount = Array.isArray(ms.comments) ? ms.comments.length : (ms.commentsCount || 0);
                    const filesCount = Array.isArray(ms.documents) ? ms.documents.length : (Array.isArray(ms.assets) ? ms.assets.length : 0);

                    const owner = ms.milestoneOwner || ms.assignee || ms.createdBy;
                    const ownerName = owner?.name ? `${owner.name} ${owner.surname || ''}`.trim() : 'Unassigned';

                    return (
                        <WorkItemContextMenu
                            key={msId}
                            item={ms}
                            onView={onMilestoneClick}
                            onEdit={onEdit}
                            onStatusChange={onStatusChange}
                            onConvertType={onConvertType}
                            onDelete={onDelete}
                        >
                            <div
                                onClick={() => onMilestoneClick?.(ms)}
                                className="w-full bg-white dark:bg-[#1e293b] rounded-2xl border border-amber-200/80 dark:border-amber-900/60 p-5 shadow-xs hover:shadow-md hover:border-amber-400 transition-all cursor-pointer flex flex-col justify-between space-y-4 group"
                            >
                                {/* Top Header */}
                                <div className="space-y-2">
                                    <div className="flex items-start justify-between gap-3">
                                        <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border border-blue-200 dark:border-blue-800 shrink-0">
                                            {projectName}
                                        </span>
                                        {getStatusTag(ms.status)}
                                    </div>

                                    <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-start gap-2 group-hover:text-[#03488a] dark:group-hover:text-[#a7c8ff] transition-colors leading-snug">
                                        <span className="text-amber-500 font-black text-lg shrink-0 mt-0.5">◆</span>
                                        <span className="line-clamp-2">{title}</span>
                                    </h3>
                                </div>

                                {/* Progress Bar */}
                                <div className="space-y-1.5 py-1">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500 dark:text-slate-400 font-medium">Phase Progress</span>
                                        <span className="font-extrabold text-emerald-600 dark:text-emerald-400">{progress}%</span>
                                    </div>
                                    <Progress
                                        percent={progress}
                                        showInfo={false}
                                        strokeColor={{ '0%': '#03488a', '100%': '#10b981' }}
                                        trailColor="rgba(203, 213, 225, 0.3)"
                                        size="small"
                                    />
                                </div>

                                {/* Deliverables summary if available */}
                                {totalDeliverables > 0 && (
                                    <div className="bg-amber-50/60 dark:bg-amber-950/40 px-3 py-2 rounded-xl border border-amber-200/60 dark:border-amber-900/40 flex items-center justify-between text-xs">
                                        <span className="text-amber-900 dark:text-amber-300 font-medium flex items-center gap-1.5">
                                            <CheckCircleOutlined className="text-emerald-500" /> Deliverables
                                        </span>
                                        <span className="font-bold text-amber-950 dark:text-amber-200">
                                            {completedDeliverables}/{totalDeliverables} Met
                                        </span>
                                    </div>
                                )}

                                {/* Owner & Due Date */}
                                <div className="flex items-center justify-between text-xs pt-3 border-t border-slate-100 dark:border-slate-800 text-slate-500">
                                    <div className="flex items-center gap-2">
                                        <Avatar
                                            size={24}
                                            src={owner?.photo || owner?.avatar}
                                            icon={<UserOutlined />}
                                            className="bg-[#03488a] text-white shrink-0"
                                        />
                                        <span className="font-semibold text-slate-700 dark:text-slate-300 truncate max-w-[120px]">
                                            {ownerName}
                                        </span>
                                    </div>

                                    {ms.dueDate && (
                                        <span className="flex items-center gap-1 text-slate-500 font-medium">
                                            <ClockCircleOutlined className="text-slate-400" />
                                            {new Date(ms.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </span>
                                    )}
                                </div>

                                {/* Footer Metric Badges */}
                                <div className="flex items-center justify-between text-xs pt-2.5 border-t border-slate-100 dark:border-slate-800">
                                    <Tooltip title={`${completedChildTasks} of ${totalChildTasks} Tasks Completed`}>
                                        <span className="flex items-center gap-1 text-slate-600 dark:text-slate-400 font-bold bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-md">
                                            <UnorderedListOutlined className="text-blue-500" /> {completedChildTasks}/{totalChildTasks} Tasks
                                        </span>
                                    </Tooltip>

                                    <div className="flex items-center gap-3 text-slate-400 font-medium">
                                        <Tooltip title={`${commentsCount} Comments`}>
                                            <span className="flex items-center gap-1 hover:text-slate-600 dark:hover:text-slate-200">
                                                <MessageOutlined /> {commentsCount}
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={`${filesCount} Attached Files`}>
                                            <span className="flex items-center gap-1 hover:text-slate-600 dark:hover:text-slate-200">
                                                <PaperClipOutlined /> {filesCount}
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        </WorkItemContextMenu>
                    );
                })}
            </div>
        </div>
    );
}
